import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import axiosUI from '../../axiosUI';
import { Link } from '@reach/router';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import Main from '../Main/Main';
import Button from '../Button/Button';
import PopupWrapper from '../PopupWrapper';
import Loader from '../Loader/Loader';
import { withGlobalContext } from '../../App';
import {
  NOTIFICATIONS,
  SALESCHANNEL_NAME_MAP,
  LOCALE_NAME_MAP,
  RESOURCE_STATES,
} from '../../constants';
import OpeningHourChangesetTable from './OpeningHourChangesetTable';

class OpeningHourChangeset extends Component {
  static propTypes = {
    language: PropTypes.string,
    country: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      drafts: [],
      selected: {},
      loading: false,
      showDiscardWarningPopup: false,
    };
  }

  componentDidMount = () => {
    this.setState({ loading: true }, async () => {
      try {
        await this.fetchDraftsAndInitializeState();
      } catch (err) {
        console.log(err);
      }

      this.setState({ loading: false });
    });
  };

  fetchDraftsAndInitializeState = async () => {
    const state = RESOURCE_STATES.DRAFT;
    const { data } = await axiosUI.get('/api/opening-hours', {
      params: {
        salesChannel: this.props.country,
        locale: this.props.language,
        state,
      },
    });

    this.setState({
      drafts: data,
      selected: data.reduce((map, template) => {
        map[template.id] = true;
        return map;
      }, {}),
    });
  };

  toggleDiscardWarningPopup = () => {
    this.setState(prevState => ({
      showDiscardWarningPopup: !prevState.showDiscardWarningPopup,
    }));
  };

  getSelectedItems = () => {
    return this.state.drafts.filter(draft => this.state.selected[draft.id]);
  };

  discardChanges = async () => {
    this.setState({ loading: true }, async () => {
      const itemsToDelete = this.getSelectedItems();

      try {
        await axiosUI.delete(`/api/opening-hours/drafts`, {
          data: itemsToDelete,
        });

        await this.fetchDraftsAndInitializeState();
        this.toggleDiscardWarningPopup();

        this.props.createNotification({
          message: `Changes are discarded`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });
      } catch (err) {
        console.log(err);
        this.toggleDiscardWarningPopup();

        this.props.createNotification({
          message: `Failed to discard changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  approveChanges = () => {
    this.setState({ loading: true }, async () => {
      const itemsToApprove = this.getSelectedItems();

      try {
        await axiosUI.post(`/api/opening-hours/approve`, itemsToApprove);

        await this.fetchDraftsAndInitializeState();

        this.props.createNotification({
          message: `Changes are approved`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to approve changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  publishChanges = () => {
    this.setState({ loading: true }, async () => {
      const itemsToPublish = this.getSelectedItems();

      try {
        await axiosUI.post(`/api/opening-hours/publish`, itemsToPublish);

        await this.fetchDraftsAndInitializeState();

        this.props.createNotification({
          message: `Changes are published`,
          type: NOTIFICATIONS.TYPE.SUCCESS,
          timeout: 5000,
        });
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to publish changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  toggleSelectAllRows = initialValue => {
    const selections = this.state.drafts.reduce((map, template) => {
      map[template.id] = !initialValue;
      return map;
    }, {});
    this.setState({ selected: selections });
  };

  toggleRowSelection = event => {
    this.setState({
      selected: {
        ...this.state.selected,
        [event.target.id]: event.target.checked,
      },
    });
  };

  render() {
    const selectedItems = this.getSelectedItems();
    const allAreSelected = selectedItems.length === this.state.drafts.length;
    const nonNullSelectedItems = Boolean(selectedItems.length);
    const canPublishSelectedChanges =
      nonNullSelectedItems &&
      selectedItems.every(
        draft => draft.first_approver && draft.second_approver
      );
    return (
      <Fragment>
        <Sidebar highlightNavItemIndex={3} />
        <Topbar className="opening_hour_changeset__topbar">
          <span className="font-bold text-lg capitalize">
            <Link className="bb-1-blue no-underline text-blue" to="/channeling">
              Channeling
            </Link>{' '}
            /{' '}
            <Link
              className="bb-1-blue no-underline text-blue"
              to={`/channeling/${this.props.country}/${this.props.language}`}>
              {SALESCHANNEL_NAME_MAP[this.props.country]} -{' '}
              {LOCALE_NAME_MAP[this.props.language]}
            </Link>{' '}
            /{' '}
            <Link
              className="bb-1-blue no-underline text-blue"
              to={`/channeling/${this.props.country}/${this.props.language}/opening-hours`}>
              Opening Hours
            </Link>{' '}
            / Review Changes
          </span>
          <span className="float-right text-right">
            <PopupWrapper
              className="relative"
              isOpen={this.state.showDiscardWarningPopup}
              togglePopup={this.toggleDiscardWarningPopup}>
              <Button
                type="red-transparent"
                className="h-8"
                disabled={!nonNullSelectedItems}
                onClick={this.toggleDiscardWarningPopup}>
                Discard Changes
              </Button>
              <AnimatePresence>
                {this.state.showDiscardWarningPopup && (
                  <motion.div
                    initial={{ opacity: 0, y: '80%', bottom: '-10px' }}
                    animate={{ opacity: 1, y: '100%' }}
                    exit={{ opacity: 0, y: '80%' }}
                    transition={{
                      ease: [0.18, 0.89, 0.39, 1.4],
                      duration: 0.3,
                    }}
                    className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                    <div className="text-center leading-normal">
                      Are you sure you want to discard selected changes?
                    </div>
                    <div className="flex justify-around mt-8">
                      <Button
                        className="h-8"
                        onClick={this.toggleDiscardWarningPopup}>
                        No
                      </Button>
                      <Button
                        type="red-transparent"
                        className="h-8"
                        onClick={this.discardChanges}>
                        Yes, discard them
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </PopupWrapper>
            <Button
              type="blue"
              className="h-8 h-8 ml-8"
              disabled={!nonNullSelectedItems}
              onClick={this.approveChanges}>
              Approve Changes
            </Button>
            <Button
              type="green"
              className="h-8 h-8 ml-8"
              onClick={this.publishChanges}
              disabled={!canPublishSelectedChanges}>
              Publish Changes
            </Button>
          </span>
        </Topbar>
        <Main className="opening_hour_changeset__main relative">
          <Loader loading={this.state.loading}>
            <div className="inline-flex items-center bg-white p-2 rounded mb-4">
              <div className="w-4 h-4 bg-lightGreen rounded-full mr-2" />
              <div className="mr-6 text-sm">Additions</div>
              <div className="w-4 h-4 bg-lightYellow rounded-full mr-2" />
              <div className="mr-6 text-sm">Modifications</div>
              <div className="w-4 h-4 bg-lightRed rounded-full mr-2" />
              <div className="text-sm">Deletions</div>
            </div>
            <OpeningHourChangesetTable
              allAreSelected={allAreSelected}
              toggleSelectAllRows={this.toggleSelectAllRows}
              drafts={this.state.drafts}
              toggleRowSelection={this.toggleRowSelection}
              selected={this.state.selected}
            />
          </Loader>
        </Main>
      </Fragment>
    );
  }
}

export default  withGlobalContext(OpeningHourChangeset);
